// Notification sound options
export const SOUND_OPTIONS = [
  { id: 'chirp', label: 'Chirp', file: '/sounds/sound-chirp.mp3' },
  { id: 'key', label: 'Key', file: '/sounds/sound-key.mp3' },
  { id: 'echo', label: 'Echo', file: '/sounds/sound-echo.mp3' },
  { id: 'none', label: 'None', file: null },
] as const

export type SoundPreference = typeof SOUND_OPTIONS[number]['id']

// Debounce interval in milliseconds (10 seconds)
const SOUND_DEBOUNCE_MS = 10_000

let lastPlayedAt = 0
let audioCache: Record<string, HTMLAudioElement> = {}
let audioUnlocked = false

/**
 * Request notification permission (also unlocks audio)
 * Returns true if permission was granted
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return false
  }

  if (Notification.permission === 'granted') {
    audioUnlocked = true
    return true
  }

  if (Notification.permission === 'denied') {
    return false
  }

  try {
    const permission = await Notification.requestPermission()
    if (permission === 'granted') {
      audioUnlocked = true
      // Play silent audio to fully unlock audio context
      unlockAudio()
      return true
    }
  } catch {
    // Notification API not supported
  }

  return false
}

/**
 * Check if notifications are enabled
 */
export function notificationsEnabled(): boolean {
  return typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'granted'
}

/**
 * Check if audio playback is unlocked
 */
export function isAudioUnlocked(): boolean {
  return audioUnlocked
}

/**
 * Unlock audio playback on first user interaction
 * Must be called from a user gesture (click, keydown, etc.)
 */
function unlockAudio() {
  if (audioUnlocked) return

  // Create and play a silent audio to unlock the audio context
  const silentAudio = new Audio()
  silentAudio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='
  silentAudio.volume = 0
  silentAudio.play()
    .then(() => {
      audioUnlocked = true
    })
    .catch(() => {
      // Still locked, will try again on next interaction
    })
}

// Unlock audio on first user interaction
if (typeof window !== 'undefined') {
  const unlockEvents = ['click', 'touchstart', 'keydown']
  const handleUnlock = () => {
    unlockAudio()
    // Remove listeners after first interaction
    if (audioUnlocked) {
      unlockEvents.forEach(event => document.removeEventListener(event, handleUnlock))
    }
  }
  unlockEvents.forEach(event => document.addEventListener(event, handleUnlock, { once: false, passive: true }))
}

/**
 * Play a notification sound with debounce
 * Won't play more than once every 10 seconds
 */
export function playNotificationSound(preference: SoundPreference): void {
  if (preference === 'none') return

  const now = Date.now()
  if (now - lastPlayedAt < SOUND_DEBOUNCE_MS) return

  const sound = SOUND_OPTIONS.find(s => s.id === preference)
  if (!sound?.file) return

  // Use cached audio element or create new one
  if (!audioCache[sound.id]) {
    audioCache[sound.id] = new Audio(sound.file)
  }

  const audio = audioCache[sound.id]
  audio.currentTime = 0
  audio.play().catch(() => {
    // Ignore autoplay errors - audio not unlocked
  })

  lastPlayedAt = now
}

/**
 * Preview a sound (bypasses debounce)
 * Also unlocks audio for future playback
 */
export function previewSound(preference: SoundPreference): void {
  if (preference === 'none') return

  const sound = SOUND_OPTIONS.find(s => s.id === preference)
  if (!sound?.file) return

  const audio = new Audio(sound.file)
  audio.play()
    .then(() => {
      // Successfully played - audio is now unlocked!
      audioUnlocked = true
    })
    .catch(() => {
      // Ignore autoplay errors
    })
}
